import { useHandler } from "@redotech/react-util/hook";
import classNames from "classnames";
import { memo, useEffect, useRef, useState } from "react";
import * as styles from "./panel-resizer.module.css";

export interface PanelResizerProps {
  onResize: (delta: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  open?: boolean;
  transition?: string;
}

export const PanelResizer = memo(function PanelResizer({
  onResize,
  onResizeStart,
  onResizeEnd,
  open = true,
  transition,
}: PanelResizerProps) {
  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useHandler((e: React.MouseEvent) => {
    if (!open) {
      return;
    }
    e.preventDefault();
    isDraggingRef.current = true;
    lastXRef.current = e.clientX;
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    onResizeStart?.();
  });

  const stopResize = useHandler(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsResizing(false);
      document.body.style.cursor = "";
      onResizeEnd?.();
    }
  });

  const resize = useHandler((e: MouseEvent) => {
    if (!isDraggingRef.current) {
      return;
    }
    const delta = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    onResize(delta);
  });

  useEffect(() => {
    addEventListener("mousemove", resize);
    addEventListener("mouseup", stopResize);
    return () => {
      removeEventListener("mousemove", resize);
      removeEventListener("mouseup", stopResize);
    };
  }, [resize, stopResize]);

  return (
    <div
      className={classNames(styles.resizer, !open && styles.closed)}
      data-resizing={isResizing || undefined}
      data-web="PanelResizer"
      onMouseDown={startResize}
      style={transition ? { transition: `opacity ${transition}` } : undefined}
    />
  );
});
